import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle, Circle, Hourglass, CompassTool, Calendar, CurrencyInr,
  Sparkle, Buildings, Flag, ChatCircleDots, SignOut, Wallet, Receipt, Key,
  FileText, DownloadSimple,
} from "@phosphor-icons/react";
import { http, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ClientRequestForm from "../components/ClientRequestForm";
import ProjectDeliverables from "../components/ProjectDeliverables";
import ClientSiteVisits from "../components/ClientSiteVisits";
import MilestoneApproveButton from "../components/MilestoneApproveButton";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import { toast } from "sonner";

const statusMeta = {
  todo: { label: "Not started", icon: Circle, color: "text-zinc-400 border-zinc-300" },
  in_progress: { label: "In progress", icon: Hourglass, color: "text-blue-700 border-blue-600" },
  complete: { label: "Complete", icon: CheckCircle, color: "text-emerald-700 border-emerald-600" },
};

const PROGRESS_INITIAL = { width: 0 };
const PROGRESS_TRANSITION = { duration: 0.8, ease: "easeOut" };

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
function money(n) {
  if (!n && n !== 0) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
}

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showChangePwd, setShowChangePwd] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/client/project");
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50">
        <div className="border-2 border-zinc-900 shadow-brutal bg-white max-w-md p-8 text-center">
          <div className="label-overline text-red-600 mb-2">Portal Error</div>
          <h1 className="font-display text-2xl font-black tracking-tighter uppercase text-zinc-900">
            {error}
          </h1>
          <button onClick={async () => { await logout(); nav("/login"); }}
                  className="mt-6 label-overline bg-zinc-900 text-white px-4 py-2 hover:bg-blue-600">
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="label-overline text-zinc-500 ants px-6 py-4">Loading Project…</div>
      </div>
    );
  }

  const { project, progress, billing, tasks, deliverables = [], requests = [], share_token,
          fee_summary, advances = [] } = data;
  const milestones = tasks.filter((t) => t.is_milestone);
  const otherTasks = tasks.filter((t) => !t.is_milestone);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="client-portal">
      {/* Header */}
      <header className="border-b border-zinc-300 bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 flex items-start gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 border-2 border-orange-500 flex items-center justify-center">
                <CompassTool size={18} weight="bold" className="text-orange-500" />
              </div>
              <span className="label-overline text-orange-500">Dream Home Planners</span>
              <span className="label-overline text-zinc-500 ml-auto">Client Portal · Signed in</span>
            </div>
            <div className="label-overline text-blue-400 mb-2 flex items-center gap-2">
              <Buildings size={12} weight="bold" /> Project
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none break-words">
              {project}
            </h1>
            <p className="mt-4 text-zinc-400 max-w-2xl text-sm leading-relaxed">
              Welcome back, {user?.name}. Live progress on your project — updated as our team completes each phase.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button onClick={() => setShowChangePwd(true)}
                    data-testid="portal-change-password"
                    className="flex items-center gap-2 border border-white/40 bg-transparent text-white px-3 py-2 label-overline hover:bg-white hover:text-zinc-900 transition-colors">
              <Key size={12} weight="bold" /> Change Password
            </button>
            <button onClick={async () => { await logout(); nav("/login"); }}
                    data-testid="portal-logout"
                    className="flex items-center gap-2 border border-white/40 bg-transparent text-white px-3 py-2 label-overline hover:bg-white hover:text-zinc-900 transition-colors">
              <SignOut size={12} weight="bold" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <ChangePasswordDialog open={showChangePwd} onOpenChange={setShowChangePwd} />

      <PortalBody
        project={project}
        progress={progress}
        billing={billing}
        feeSummary={fee_summary}
        advances={advances}
        milestones={milestones}
        otherTasks={otherTasks}
        deliverables={deliverables}
        requests={requests}
        shareToken={share_token}
        onSubmitted={load}
      />
    </div>
  );
}

function PortalBody({ project, progress, billing, feeSummary, advances, milestones, otherTasks, deliverables, requests, shareToken, onSubmitted }) {
  return (
    <section className="max-w-5xl mx-auto px-6 lg:px-10 py-10 space-y-12">
      {/* Progress + Billing */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-300 bg-white">
        <div className="p-6 border-b md:border-b-0 md:border-r border-zinc-300">
          <div className="flex items-center justify-between">
            <div className="label-overline text-zinc-500">Overall Progress</div>
            <div className="label-overline text-blue-600">{progress.pct}%</div>
          </div>
          <div className="font-display text-5xl font-black tracking-tighter text-zinc-900 mt-3">
            {progress.complete}<span className="text-zinc-400">/{progress.total}</span>
          </div>
          <div className="label-overline text-zinc-500 mt-1">Tasks Completed</div>
          <div className="mt-6 h-2 bg-zinc-200 relative overflow-hidden">
            <motion.div initial={PROGRESS_INITIAL} animate={{ width: `${progress.pct}%` }}
                        transition={PROGRESS_TRANSITION}
                        className="absolute inset-y-0 left-0 bg-blue-600" />
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="label-overline text-zinc-500 flex items-center gap-1.5">
              <CurrencyInr size={12} weight="bold" /> Billing Milestones
            </div>
            <div className="label-overline text-orange-600">
              {billing.milestone_complete}/{billing.milestone_count} DONE
            </div>
          </div>
          <div className="font-display text-4xl font-black tracking-tighter text-zinc-900 mt-3 break-all">
            {money(billing.billed_amount)}
          </div>
          <div className="label-overline text-zinc-500 mt-1">Invoiced To Date</div>
          <div className="mt-6 grid grid-cols-2 gap-0 border border-zinc-300">
            <div className="p-3 border-r border-zinc-300">
              <div className="label-overline text-zinc-500">Contract Value</div>
              <div className="font-display font-bold text-zinc-900 mt-1">{money(billing.total_value)}</div>
            </div>
            <div className="p-3">
              <div className="label-overline text-zinc-500">Remaining</div>
              <div className="font-display font-bold text-orange-700 mt-1">{money(billing.remaining_amount)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Fee Collection Summary */}
      {feeSummary && (feeSummary.total_fee > 0 || feeSummary.advance_total > 0 || feeSummary.invoiced > 0) && (
        <div>
          <SectionTitle icon={Wallet} color="text-emerald-700" title="Fee Collection Summary" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-300 bg-white">
            <SummaryStat label="Total Fee" value={money(feeSummary.total_fee)} accent="text-zinc-900" testId="portal-total-fee" />
            <SummaryStat label="Advance Received" value={money(feeSummary.advance_total)} accent="text-emerald-700" testId="portal-advance-total" />
            <SummaryStat label="Invoices Paid" value={money(feeSummary.invoice_paid)} accent="text-blue-700" testId="portal-invoice-paid" />
            <SummaryStat label="Outstanding" value={money(feeSummary.outstanding)}
                         accent={feeSummary.outstanding > 0 ? "text-orange-700" : "text-emerald-700"}
                         testId="portal-outstanding" />
          </div>

          {advances.length > 0 && (
            <div className="mt-4 border border-zinc-300 bg-white" data-testid="portal-advances-list">
              <div className="px-5 py-3 border-b border-zinc-300 bg-zinc-50 flex items-center gap-2">
                <Receipt size={13} weight="bold" className="text-emerald-700" />
                <span className="label-overline text-zinc-900">Advance Receipts</span>
                <span className="ml-auto label-overline text-zinc-500">{advances.length} entr{advances.length === 1 ? "y" : "ies"}</span>
              </div>
              <div className="divide-y divide-zinc-200">
                {advances.map((a) => (
                  <div key={a.id} data-testid={`portal-advance-${a.id}`}
                       className="px-5 py-3 flex items-center gap-4">
                    <div className="w-24 label-overline text-zinc-500 font-mono">{a.date || "—"}</div>
                    <div className="font-display font-bold text-emerald-800 w-28">{money(a.amount)}</div>
                    <div className="flex-1 text-sm text-zinc-700 truncate">{a.note || "Advance received"}</div>
                    <div className="text-xs text-zinc-500 font-mono">Recorded by {a.received_by || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Milestones */}
      {milestones.length > 0 && (
        <div>
          <SectionTitle icon={Flag} color="text-orange-600" title="Billing Milestones" />
          <div className="border border-zinc-300 bg-white">
            {milestones.map((m, i) => (
              <TaskItem
                key={m.id} task={m} isMilestone
                divider={i < milestones.length - 1}
                shareToken={shareToken} onSubmitted={onSubmitted}
              />
            ))}
          </div>
        </div>
      )}

      {/* Deliverables */}
      <div>
        <SectionTitle icon={Sparkle} color="text-blue-600" title="Uploaded Deliverables" />
        <ProjectDeliverables
          projectName={project}
          canUpload={false}
          canDelete={false}
          publicToken={null}
          forceLoad={deliverables}
          paymentGate={(feeSummary?.overdue_invoice_count || 0) > 0}
        />
      </div>

      {/* Site visits — auto-updated as studio schedules / records them */}
      <div className="border border-zinc-300 bg-white p-4">
        <ClientSiteVisits projectName={project} />
      </div>

      {/* Architectural Agreement — download the signed copy */}
      <ClientAgreementCard />

      {/* Requests + replies thread */}
      {requests.length > 0 && (
        <div>
          <SectionTitle icon={ChatCircleDots} color="text-purple-600" title="Your Requests & Our Replies" />
          <div className="border border-zinc-300 bg-white">
            {requests.map((r, i) => (
              <RequestThreadItem key={r.id} r={r} divider={i < requests.length - 1} />
            ))}
          </div>
        </div>
      )}

      {/* Other phases */}
      {otherTasks.length > 0 && (
        <div>
          <SectionTitle icon={Sparkle} color="text-blue-600" title="Work Phases" />
          <div className="border border-zinc-300 bg-white">
            {otherTasks.map((t, i) => (
              <TaskItem
                key={t.id} task={t}
                divider={i < otherTasks.length - 1}
                shareToken={shareToken} onSubmitted={onSubmitted}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function SectionTitle({ icon: Icon, color, title }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon size={16} weight="bold" className={color} />
      <span className="label-overline text-zinc-900">{title}</span>
      <div className="flex-1 h-px bg-zinc-300 ml-2" />
    </div>
  );
}

function SummaryStat({ label, value, accent = "text-zinc-900", testId }) {
  return (
    <div className="p-5 border-r border-b md:border-b-0 border-zinc-300 last:border-r-0" data-testid={testId}>
      <div className="label-overline text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-black tracking-tighter mt-2 break-all ${accent}`}>{value}</div>
    </div>
  );
}

function ClientAgreementCard() {
  const [status, setStatus] = useState(null); // { available, project, updated_at? } | null
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get("/client/agreement");
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus({ available: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await http.get("/client/agreement/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Agreement-${status?.project || "project"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Agreement downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  if (!status) return null; // still loading

  return (
    <div
      data-testid="portal-agreement-card"
      className="border border-zinc-300 bg-white p-5"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 shrink-0 border-2 border-orange-600 bg-orange-50 flex items-center justify-center">
          <FileText size={20} weight="bold" className="text-orange-700" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-overline text-orange-700">Architectural Agreement</div>
          <div className="font-display text-lg font-bold text-zinc-900 tracking-tight mt-0.5">
            {status.available
              ? "Your signed agreement is ready"
              : "Your agreement is not yet prepared"}
          </div>
          <div className="text-xs text-zinc-500 font-mono mt-1">
            {status.available
              ? `Last updated ${status.updated_at ? new Date(status.updated_at).toLocaleDateString() : "recently"}. Keep a copy for your records.`
              : "Once the studio finalises the terms, you'll be able to download the signed PDF from here."}
          </div>
        </div>
        {status.available && (
          <button
            type="button"
            data-testid="portal-agreement-download"
            onClick={download}
            disabled={downloading}
            className="shrink-0 h-10 px-4 border-2 border-orange-600 bg-orange-600 hover:bg-orange-700 hover:border-orange-700 text-white text-xs font-mono uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
          >
            <DownloadSimple size={13} weight="bold" />
            {downloading ? "Preparing…" : "Download PDF"}
          </button>
        )}
      </div>
    </div>
  );
}

function TaskItem({ task, isMilestone, divider, shareToken, onSubmitted }) {
  const meta = statusMeta[task.status] || statusMeta.todo;
  const Icon = meta.icon;
  return (
    <div data-testid={`portal-task-${task.id}`} className={`p-5 ${divider ? "border-b border-zinc-200" : ""}`}>
      <div className="grid grid-cols-[40px_1fr_auto] gap-4 items-center">
        <div className="flex justify-center">
          <div className={`w-10 h-10 border-2 flex items-center justify-center ${meta.color}`}>
            <Icon size={18} weight="bold" />
          </div>
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-zinc-900 truncate">{task.title}</div>
          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 font-mono flex-wrap">
            <span className={`label-overline ${meta.color.split(" ")[0]}`}>{meta.label}</span>
            {task.due_date && (
              <span className="flex items-center gap-1">
                <Calendar size={11} weight="bold" /> {formatDate(task.due_date)}
              </span>
            )}
            {task.request_count > 0 && (
              <span className="flex items-center gap-1 text-blue-700">
                <ChatCircleDots size={11} weight="bold" /> {task.request_count} request{task.request_count > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
        {isMilestone && (
          <div className={`font-display text-xl font-black tracking-tighter ${
            task.status === "complete" ? "text-emerald-700" : "text-zinc-900"
          }`}>
            {money(task.milestone_amount)}
          </div>
        )}
      </div>
      {isMilestone && (
        <MilestoneApproveButton task={task} mode="auth" onApproved={onSubmitted} />
      )}
      {shareToken && (
        <ClientRequestForm
          token={shareToken}
          taskId={task.id}
          taskTitle={task.title}
          onSubmitted={onSubmitted}
        />
      )}
    </div>
  );
}

function RequestThreadItem({ r, divider }) {
  return (
    <div data-testid={`portal-request-${r.id}`} className={`p-5 ${divider ? "border-b border-zinc-200" : ""}`}>
      <div className="label-overline text-zinc-500 mb-2">
        <Buildings size={11} weight="bold" className="inline mr-1" />
        {r.task_title || "Project"}
      </div>
      <div className="border-l-4 border-zinc-400 bg-zinc-50 px-4 py-3">
        <div className="text-xs text-zinc-500 font-mono flex items-center gap-2">
          <span className="label-overline">{r.client_name}</span>
          <span>· {new Date(r.created_at).toLocaleString()}</span>
        </div>
        <div className="mt-1.5 text-sm text-zinc-800 whitespace-pre-wrap">{r.message}</div>
      </div>
      {r.reply ? (
        <div className="mt-3 border-l-4 border-blue-600 bg-blue-50 px-4 py-3" data-testid={`portal-reply-${r.id}`}>
          <div className="label-overline text-blue-700 flex items-center gap-1.5">
            <ChatCircleDots size={11} weight="bold" /> Studio Reply
            {r.replied_by && <span className="text-blue-800 normal-case tracking-normal">· {r.replied_by}</span>}
          </div>
          <div className="mt-1.5 text-sm text-zinc-900 whitespace-pre-wrap">{r.reply}</div>
          {r.replied_at && (
            <div className="mt-1.5 text-[10px] text-zinc-500 font-mono">
              {new Date(r.replied_at).toLocaleString()}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 text-xs text-zinc-500 font-mono">
          <Clock className="inline" size={11} weight="bold" /> Awaiting reply from studio…
        </div>
      )}
    </div>
  );
}

// Icon shim for the "Awaiting reply" line
function Clock({ size, weight, className = "" }) {
  return <Hourglass size={size} weight={weight} className={className + " inline mr-1"} />;
}
